/**
 * Generic version of the "You have joined the waitlist!" toast
 * (LiveReadingsToast, HORO-10471 / Figma 4440:28056 / 4481:21316) —
 * extracted so other features (HORO-10509's auth flow) can reuse the exact
 * same visual pattern instead of re-implementing it. LiveReadingsToast now
 * wraps this component rather than duplicating its markup/CSS.
 *
 * Purely presentational + self-contained dismiss timer — callers own
 * "should this be visible" via `show`/`onDismiss` (typically a boolean
 * flag in Redux), same as the original.
 */
import classNames from 'classnames';
import { motion } from 'framer-motion';
import { useEffect } from 'react';
import { createPortal } from 'react-dom';

import styles from './styles.module.scss';

const DEFAULT_DISMISS_DURATION = 4000;

const ToastCloseIcon = () => (
    <svg width={24} height={24} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <path d="M18 6 6 18M6 6l12 12" stroke="#FFFFFF" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

// HORO-10509: opt-in modifier — the original "joined the waitlist" toast's
// progress strip is deliberately inset (239 of 371px, Figma 4440:28056 /
// 4481:21316), so that stays the default. The sign-in confirmation toast
// (Figma 4564:52812 desktop / 4510:29794 mobile) asks for the loading bar
// to run the toast's full width instead — opt in per-caller rather than
// changing the shared default and quietly altering LiveReadingsToast too.
const Toast = ({ show, message, onDismiss, dismissLabel, duration = DEFAULT_DISMISS_DURATION, fullWidthProgress = false }) => {
    // Belt-and-braces auto-dismiss — see LiveReadingsToast's original note:
    // the progress bar's onAnimationComplete is the primary trigger, but
    // framer-motion won't fire it if the tab is backgrounded mid-animation.
    useEffect(() => {
        if (!show) return undefined;
        const timer = setTimeout(onDismiss, duration + 250);
        return () => clearTimeout(timer);
    }, [show, duration, onDismiss]);

    if (!show) return null;

    const body = (
        // Centring lives on the wrapper, not the toast itself — framer-motion
        // writes an inline `transform` to animate `y`, which would beat a
        // stylesheet `translateX(-50%)` on the same element.
        <div className={styles.viewport}>
            <motion.div
                className={styles.toast}
                initial={{ opacity: 0, y: -16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.25, ease: 'easeOut' }}
                role="status"
                aria-live="polite"
            >
                <span className={styles.label}>{message}</span>
                <button type="button" className={styles.close} onClick={onDismiss} aria-label={dismissLabel}>
                    <ToastCloseIcon />
                </button>

                <motion.div
                    className={classNames(styles.progressBar, { [styles.progressBarFullWidth]: fullWidthProgress })}
                    initial={{ scaleX: 0 }}
                    animate={{ scaleX: 1 }}
                    transition={{ duration: duration / 1000, ease: 'linear' }}
                    style={{ originX: 0 }}
                    onAnimationComplete={onDismiss}
                />
            </motion.div>
        </div>
    );

    if (typeof document === 'undefined') return body;
    return createPortal(body, document.body);
};

export default Toast;
